import {Component, effect, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {ButtonModule} from 'primeng/button';
import {MenubarModule} from 'primeng/menubar';
import {NavigationEnd, Router} from '@angular/router';
import {TranslocoService} from '@jsverse/transloco';
import {UserContextMenuComponent} from './user-context-menu/user-context-menu.component';
import {ProviderTenantSwitcherComponent} from './provider-tenant-switcher/provider-tenant-switcher.component';
import {Subscription} from 'rxjs';
import Keycloak from 'keycloak-js';
import {KEYCLOAK_EVENT_SIGNAL, KeycloakEventType} from 'keycloak-angular';
import {MenuItem} from 'primeng/api';
import {filter} from 'rxjs/operators';
import {sortItems} from '../../shared/utils/array-utils';
import {DirectusService, HeaderMenu} from '@frontend/directus';
import {HeaderMenuService} from './header-menu.service';
import {APP_ROUTES} from '../../app.routes';

/** Menu entries that should stay highlighted while the user is on a related sub-route. */
const RELATED_ACTIVE_ROUTES: Record<string, string[]> = {
  [APP_ROUTES.QUICK_CHECK_INFO]: [APP_ROUTES.QUICK_CHECK],
};

@Component({
  selector: 'app-header',
  imports: [ButtonModule, MenubarModule, UserContextMenuComponent, ProviderTenantSwitcherComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnDestroy, OnInit {
  private readonly transloco = inject(TranslocoService);
  private readonly directusService = inject(DirectusService);
  private readonly router = inject(Router);
  private readonly keycloak = inject(Keycloak);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly headerMenuService = inject(HeaderMenuService);

  activeUrl = '';
  activeMenuItemStyle = 'active-menu-item';
  items = signal<MenuItem[]>([]);
  private allHeaderItems: MenuItem[] = [];
  private langSubscription!: Subscription;

  constructor() {
    effect(() => {
      this.handleMenuItemsByAuthenticationState();
    });
  }

  ngOnInit() {
    this.activeUrl = this.router.url;
    this.loadMenuItems();
    this.handleActiveItemOnRoutingChange();
    this.handleMenuItemTranslation();
    this.updateActiveItem();
  }

  handleMenuItemTranslation() {
    this.langSubscription = this.transloco.langChanges$.subscribe(() => {
      this.translateMenuItems();
    });
  }

  handleActiveItemOnRoutingChange() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.activeUrl = event.url;
        this.updateActiveItem();
      });
  }

  handleMenuItemsByAuthenticationState() {
    const keycloakEvent = this.keycloakSignal();
    if (
      this.allHeaderItems.length &&
      (keycloakEvent.type === KeycloakEventType.Ready ||
        keycloakEvent.type === KeycloakEventType.AuthLogout)
    ) {
      this.setItems();
      this.updateActiveItem();
    }
  }

  setItems() {
    if (!this.allHeaderItems.length) {
      return;
    }

    this.items.set(
      this.headerMenuService.getFilteredMenuItems(
        this.allHeaderItems,
        this.keycloak.authenticated ?? false,
      ),
    );
  }

  loadMenuItems() {
    this.directusService.loadHeaderMenu().subscribe({
      next: (response: HeaderMenu[]) => {
        const sortedHeaderMenuItems = sortItems(response);
        this.allHeaderItems = sortedHeaderMenuItems.map((item: HeaderMenu) => {
          const isExternalUrl = item.url && item.url.startsWith('http');

          return {
            translationKey: item.translationKey,
            icon: '',
            ...(isExternalUrl ? {url: item.url || undefined} : {routerLink: item.url || undefined}),
            styleClass: item.url === APP_ROUTES.START ? this.activeMenuItemStyle : '',
            loggedIn: item.loggedIn,
            sortNumber: item.sortNumber,
          };
        });

        this.setItems();
        this.translateMenuItems();
        this.updateActiveItem();
      },
    });
  }

  translateMenuItems() {
    const lang = this.transloco.getActiveLang();
    this.transloco.load(lang).subscribe(() => {
      this.allHeaderItems = this.allHeaderItems.map((item) => ({
        ...item,
        label: this.transloco.translate(item['translationKey']),
      }));
      this.setItems();
      this.updateActiveItem(); // needed to highlight correct menu item as active on first page load
    });
  }

  private isActiveRoute(routerLink: string | undefined): boolean {
    if (!routerLink) {
      return false;
    }
    if (routerLink === this.activeUrl) {
      return true;
    }
    return (RELATED_ACTIVE_ROUTES[routerLink] ?? []).some(
      (route) => this.activeUrl === route || this.activeUrl.startsWith(route + '/'),
    );
  }

  updateActiveItem() {
    const currentItems = this.items();
    const updated = currentItems.map((item) => ({
      ...item,
      styleClass: this.isActiveRoute(item.routerLink) ? this.activeMenuItemStyle : '',
    }));
    this.items.set(updated);

    if (this.activeUrl === '/') {
      const startIndex = updated.findIndex(
        (item) => item.routerLink === APP_ROUTES.START || item.routerLink === '/',
      );
      if (startIndex !== -1) {
        updated[startIndex] = {...updated[startIndex], styleClass: this.activeMenuItemStyle};
        this.items.set(updated);
      }
    }
  }

  ngOnDestroy() {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }
}
